import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useState } from 'react';
import { VectorModalContext } from '../../../hooks/useVectorMemoryModal';
import { useNotificationStore } from '../../../store/storeNotifications';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Tab,
  Tabs,
} from '@mui/material';
import { Code, Database, FileText, Gear, X } from '@phosphor-icons/react';
import { EditStoredFilesTabPanel } from './EditStoredFilesTabPanel';
import { EmbeddingTabPanel } from './EmbeddingTabPanel';
import { SettingsTabPanel } from './SettingsTabPanel';
import { UploadTabPanel } from './UploadTabPanel';
import { VectorChunkingBulkEditModal } from './VectorChunkingBulkEditModal';
import { VectorDimensionBulkEditModal } from './VectorDimensionBulkEditModal';
import { VectorMetadataBulkEditModal } from './VectorMetadataBulkEditModal';
import { VectorModalButton, VectorModalButtonLabel } from './VectorModalButton';

import {
  createNamespace,
  type CreateNamespaceBody,
  type VectorApiParams,
} from '../../../api/createNamespace';
import {
  createVectorMetadatas,
  type CreateVectorMetadatasBody,
} from '../../../api/createVectorMetadatas';
import {
  deleteVectorFile,
  type DeleteVectorFileBody,
  type DeleteVectorFileRouteParam,
} from '../../../api/deleteVectorFile';
import {
  deleteVectorMetadatas,
  type DeleteVectorMetadatasBody,
} from '../../../api/deleteVectorMetadatas';
import {
  updateVectorMetadatasName,
  type UpdateVectorMetadatasNameBody,
} from '../../../api/updateVectorMetadatasName';
import {
  updateVectorMetadatasValue,
  type UpdateVectorMetadatasValueBody,
} from '../../../api/updateVectorMetadatasValue';
import { TIME_DIMENSION } from '../../../constants/fixedDimensions';
import { spacesQueries } from '../../../queries/spaces';
import { vectorQueries } from '../../../queries/vector';
import {
  VectorService,
  type VectorDeleteDimensionsRequest,
} from '../../../service/VectorService';

import type { NamespaceFile } from '../../../types/namespace';
import type { NamespaceDimension } from '../../../types/namespace-dimension';
import type {
  VectorChunkingMethod,
  VectorEmbeddingModel,
  VectorMetadata,
} from '../../../types/vector';
import type { TabName, VectorFileData } from './vectorModal';

interface VectorMemoryModalProps {
  namespaceId?: string;
  spaceId?: string;
  open: boolean;
  onOpenChange: ({
    open,
    namespaceId,
  }: {
    open: boolean;
    namespaceId?: string;
  }) => void;
}

const selectChunkingOptions: Array<{
  label: string;
  value: VectorChunkingMethod;
}> = [
  { label: '1000 Characters', value: 'by1000Characters' },
  { label: 'Entire Document', value: 'byEntireDocument' },
  { label: 'Paragraph', value: 'byParagraph' },
  { label: 'Sentence', value: 'bySentence' },
  { label: 'Heading', value: 'byHeading' },
];

const BULK_MAX_DISPLAY_ITEMS = 3;

function VectorMemoryModal({
  namespaceId,
  spaceId,
  open,
  onOpenChange,
}: VectorMemoryModalProps) {
  const modalId = useId();

  const setNotification = useNotificationStore(state => state.setNotification);

  const queryClient = useQueryClient();

  const { data: userNamespaces } = useQuery({
    ...vectorQueries.allNamespaces(),
    enabled: !spaceId && open,
    refetchInterval: 1000 * 30, // 30 seconds
  });
  const { data: space } = useQuery({
    ...spacesQueries.list(spaceId!),
    enabled: !!spaceId && open,
    refetchInterval: 1000 * 30, // 30 seconds
  });

  const namespaces = spaceId ? space?.data.namespaces : userNamespaces?.data;

  const [dimensionBulkEditModalOpen, setDimensionBulkEditModalOpen] =
    useState(false);
  const [chunkingBulkEditModalOpen, setChunkingBulkEditModalOpen] =
    useState(false);
  const [metadataBulkEditModalOpen, setMetadataBulkEditModalOpen] =
    useState(false);

  const isEditing = namespaceId !== undefined;
  const selectedNamespace = namespaces?.find(({ id }) => namespaceId === id);

  const handleDimensionBulkEditModalOpen = (open?: boolean) => {
    if (open) {
      setDimensionBulkEditModalOpen(open);

      return;
    }

    setDimensionBulkEditModalOpen(prev => !prev);
  };

  const handleChunkingBulkEditModalOpen = (open?: boolean) => {
    if (open) {
      setChunkingBulkEditModalOpen(open);

      return;
    }

    setChunkingBulkEditModalOpen(prev => !prev);
  };

  const handleMetadataBulkEditModalOpen = (open?: boolean) => {
    if (open) {
      setMetadataBulkEditModalOpen(open);

      return;
    }

    setMetadataBulkEditModalOpen(prev => !prev);
  };

  const handleTabChange = (tab: TabName) => {
    setTab(tab);

    setSelectedStagingFiles([]);
    setSelectedStoredFiles([]);
  };

  /* Context state */
  const [tab, setTab] = useState<TabName>('embedding');

  const [embeddingModel, setEmbeddingModel] =
    useState<VectorEmbeddingModel>('voyage-3-large');
  const [name, setName] = useState<string>('');
  const [searchUsingDimensions, setSearchUsingDimensions] = useState(
    selectedNamespace ? selectedNamespace.searchUsingDimensions : true,
  );

  const [stagingFiles, setStagingFiles] = useState<Array<VectorFileData>>([]);
  const [selectedStagingFiles, setSelectedStagingFiles] = useState<
    Array<string>
  >([]);
  const [namespaceDimensions, setNamespaceDimensions] = useState<
    Array<NamespaceDimension>
  >([]);

  const [storedFiles, setStoredFiles] = useState<Array<NamespaceFile>>([]);
  const [selectedStoredFiles, setSelectedStoredFiles] = useState<Array<string>>(
    [],
  );

  const handleEmbeddingModelChange = (embeddingModel: VectorEmbeddingModel) => {
    setEmbeddingModel(embeddingModel);
  };

  const handleNameChange = (newName: string) => {
    setName(newName);
  };

  const handleSearchUsingDimensionsChange = (state?: boolean) => {
    if (state) {
      setSearchUsingDimensions(state);
    }

    setSearchUsingDimensions(prev => !prev);
  };

  const handleStagingFilesChange = useCallback(
    (files: Array<VectorFileData>) => {
      setStagingFiles(files);
    },
    [],
  );

  const handleStagingFilesAdd = useCallback((files: Array<VectorFileData>) => {
    setStagingFiles(prev => [...prev, ...files]);
  }, []);

  const handleSelectedStagingFilesChange = (selectedFiles: Array<string>) => {
    setSelectedStagingFiles(selectedFiles);
  };

  const handleStoredFilesChange = (files: Array<NamespaceFile>) => {
    setStoredFiles(files);
  };

  const handleNamespaceDimensionsChange = (
    dimensions: Array<NamespaceDimension>,
  ) => {
    setNamespaceDimensions(dimensions);
  };

  const handleSelectedStoredFilesChange = (selectedFiles: Array<string>) => {
    setSelectedStoredFiles(selectedFiles);
  };

  const handleCloseDialog = () => {
    onOpenChange({ open: false, namespaceId: undefined });

    handleResetDialogState();
  };

  const handleResetDialogState = () => {
    setTab('embedding');
    setName('');
    setEmbeddingModel('voyage-3-large');
    setSearchUsingDimensions(true);

    setStagingFiles([]);
    setSelectedStagingFiles([]);
    setSelectedStoredFiles([]);
    setNamespaceDimensions([]);
  };

  // TODO: add dimensions
  const handleNamespaceCreate = () => {
    const filesToUpload = stagingFiles.map(
      ({
        fileUrl,
        metadatas,
        chunking,
        customFileName,
        dimensions,
        headingLevel,
      }) => ({
        fileUrl,
        chunkingMethod: chunking,
        metadatas,
        customFileName,
        dimensions,
        headingLevel,
      }),
    );

    createNamespaceFn({
      body: {
        embeddingModelName: embeddingModel,
        files: filesToUpload,
        searchUsingDimensions,
      },
      params: {
        id: crypto.randomUUID(),
        nameSpace: selectedNamespace ? selectedNamespace.name : name,
        spaceId,
      },
    });
  };

  // TODO: add dimensions
  const handleNamespaceUpdate = () => {
    if (!selectedNamespace) {
      return;
    }

    const filesToUpload = stagingFiles.map(
      ({
        fileUrl,
        metadatas,
        chunking,
        customFileName,
        dimensions,
        headingLevel,
      }) => ({
        fileUrl,
        chunkingMethod: chunking,
        metadatas,
        customFileName,
        dimensions,
        headingLevel,
      }),
    );

    createNamespaceFn({
      body: {
        embeddingModelName: embeddingModel,
        files: filesToUpload,
        searchUsingDimensions,
      },
      params: {
        id: selectedNamespace.id,
        nameSpace: selectedNamespace.name,
        spaceId,
      },
    });
  };

  const handleMetadataCreate = ({
    filesIds,
    metadatas,
  }: {
    filesIds: Array<string>;
    metadatas: Array<VectorMetadata>;
  }) => {
    if (tab === 'edit-stored-files') {
      createVectorMetadatasFn({
        embeddingModelName: embeddingModel,
        filesIds,
        metadatas,
      });

      return;
    }

    const newFileList = stagingFiles.map(file => {
      if (filesIds.includes(file.id)) {
        return {
          ...file,
          metadatas: [...file.metadatas, ...metadatas],
        };
      }

      return file;
    });

    setStagingFiles(newFileList);
  };

  const handleMetadataDelete = ({
    filesIds,
    metadatas,
  }: {
    filesIds: Array<string>;
    metadatas: Array<{ name: string }>;
  }) => {
    if (tab === 'edit-stored-files') {
      deleteVectorMetadatasFn({
        embeddingModelName: embeddingModel,
        filesIds,
        metadatas,
      });

      return;
    }

    const metadatasToDelete = metadatas.map(metadata => metadata.name);

    const newFileList = stagingFiles.map(file => {
      if (filesIds.includes(file.id)) {
        const newMetadataList = file.metadatas.filter(
          metadata => !metadatasToDelete.includes(metadata.name),
        );

        return {
          ...file,
          metadatas: newMetadataList,
        };
      }

      return file;
    });

    setStagingFiles(newFileList);
  };

  const handleMetadataNameChange = ({
    filesIds,
    metadatas,
  }: {
    filesIds: Array<string>;
    metadatas: Array<{ oldName: string; newName: string }>;
  }) => {
    if (tab === 'edit-stored-files') {
      updateVectorMetadatasNameFn({
        embeddingModelName: embeddingModel,
        filesIds,
        metadatas,
      });

      return;
    }

    const newFileList = stagingFiles.map(file => {
      if (filesIds.includes(file.id)) {
        const newMetadataList = file.metadatas.map(metadata => {
          const existingMetadata = metadatas.find(
            ({ oldName }) => oldName === metadata.name,
          );

          if (existingMetadata) {
            return {
              ...metadata,
              name: existingMetadata.newName,
            };
          }

          return metadata;
        });

        return {
          ...file,
          metadatas: newMetadataList,
        };
      }

      return file;
    });

    setStagingFiles(newFileList);
  };

  const handleMetadataValueChange = ({
    filesIds,
    metadatas,
  }: {
    filesIds: Array<string>;
    metadatas: Array<{ name: string; newValue: string }>;
  }) => {
    if (tab === 'edit-stored-files') {
      updateVectorMetadatasValueFn({
        embeddingModelName: embeddingModel,
        filesIds,
        metadatas,
      });

      return;
    }

    const newFileList = stagingFiles.map(file => {
      if (filesIds.includes(file.id)) {
        const metadatasEdited = file.metadatas.map(metadata => {
          const existingMetadata = metadatas.find(
            ({ name }) => metadata.name === name,
          );

          if (existingMetadata) {
            return {
              ...metadata,
              value: existingMetadata.newValue,
            };
          }

          return metadata;
        });

        const metadatasToAdd = metadatas.reduce(
          (prev: Array<{ name: string; value: string }>, metadata) => {
            const existingMetadata = file.metadatas.find(
              ({ name }) => name === metadata.name,
            );

            if (existingMetadata) {
              return prev;
            }

            prev.push({
              name: metadata.name,
              value: metadata.newValue,
            });

            return prev;
          },
          [],
        );

        return {
          ...file,
          metadatas: [...metadatasEdited, ...metadatasToAdd],
        };
      }

      return file;
    });

    setStagingFiles(newFileList);
  };

  const handleVectorFileDelete = async (fileId: string) => {
    if (tab === 'edit-stored-files') {
      await deleteVectorFileFn({
        body: { embeddingModelName: embeddingModel },
        params: {
          nameSpace: selectedNamespace ? selectedNamespace.name : name,
          id: namespaceId,
          spaceId,
        },
        routeParam: { fileId },
      });

      return;
    }

    const newFileList = stagingFiles.filter(file => file.id !== fileId);

    setStagingFiles(newFileList);
  };

  const handleNamespaceDimensionsDelete = (id: string, title: string) => {
    if (isEditing) {
      deleteNamespaceDimensionsFn({
        body: {
          dimensions: [{ id, title }],
          filesIds: [],
          embeddingModelName: embeddingModel,
        },
        params: { nameSpace: name, id: namespaceId, spaceId },
      });

      return;
    }

    const newFileList = stagingFiles.map(file => {
      const newDimensionList = file.dimensions.filter(
        dimension => dimension.id !== id,
      );

      return {
        ...file,
        dimensions: newDimensionList,
      };
    });

    setStagingFiles(newFileList);

    const newDimensionList = namespaceDimensions.filter(
      dimension => dimension.id !== id,
    );

    setNamespaceDimensions(newDimensionList);
  };
  /* End context state */

  const handleBulkDeleteFiles = async () => {
    if (tab === 'edit-stored-files') {
      const deletePromises = selectedStoredFiles.map(async storedFileId => {
        const file = storedFiles.find(({ id }) => id === storedFileId);

        if (!file) {
          return;
        }

        const response = await deleteVectorFileFn({
          body: { embeddingModelName: embeddingModel },
          params: {
            nameSpace: selectedNamespace ? selectedNamespace.name : name,
            spaceId,
          },
          routeParam: { fileId: file.id },
        });

        return response;
      });

      await Promise.all(deletePromises);

      const newFileList = storedFiles.filter(
        ({ id }) => !selectedStoredFiles.includes(id),
      );

      setStoredFiles(newFileList);
      setSelectedStoredFiles([]);

      return;
    }

    if (tab === 'upload') {
      const newFileList = stagingFiles.filter(
        ({ id }) => !selectedStagingFiles.includes(id),
      );

      setStagingFiles(newFileList);
      setSelectedStagingFiles([]);

      return;
    }
  };

  const { mutate: createNamespaceFn, status } = useMutation({
    mutationFn: ({
      body,
      params,
    }: {
      body: CreateNamespaceBody;
      params: VectorApiParams;
    }) => createNamespace({ body, params }),
    onMutate: variables => {
      const action = isEditing ? 'Updating' : 'Creating';

      setNotification({
        type: 'warning',
        message: `${action} memory pod: ${variables.params.nameSpace}`,
      });
    },
    onError: (error, variables) => {
      const action = isEditing ? 'updating' : 'creating';
      setNotification({
        type: 'error',
        message: `Error while ${action} memory pod file: ${variables.params.nameSpace}. ${error.message}`,
      });
    },
    onSuccess: (_, variables) => {
      const action = isEditing ? 'updated' : 'created';

      handleCloseDialog();

      setNotification({
        type: 'success',
        message: `Memory pod ${variables.params.nameSpace} was ${action} successfully`,
      });

      if (variables.params.spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  const { mutateAsync: deleteVectorFileFn } = useMutation({
    mutationFn: ({
      body,
      params,
      routeParam,
    }: {
      body: DeleteVectorFileBody;
      params: VectorApiParams;
      routeParam: DeleteVectorFileRouteParam;
    }) => deleteVectorFile({ body, params, routeParam }),
    onMutate: variables => {
      setNotification({
        type: 'warning',
        message: `Deleting file: ${variables.routeParam.fileId}`,
      });
    },
    onError: (_, variables, context) => {
      setNotification({
        type: 'error',
        message: `Error while deleting file: ${variables.routeParam.fileId}`,
      });
    },
    onSuccess: (_, variables) => {
      setNotification({
        type: 'success',
        message: `File ${variables.routeParam.fileId} was deleted successfully`,
      });

      if (variables.params.spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  const { mutate: createVectorMetadatasFn } = useMutation({
    mutationFn: (body: CreateVectorMetadatasBody) =>
      createVectorMetadatas(body),
    onMutate: variables => {
      setNotification({
        type: 'warning',
        message: `Adding label(s): ${variables.metadatas.map(({ name }) => name).join(', ')}`,
      });
    },
    onError: (_, variables) => {
      setNotification({
        type: 'error',
        message: `Error adding label(s): ${variables.metadatas.map(({ name }) => name).join(', ')}`,
      });
    },
    onSuccess: (_, variables) => {
      setNotification({
        type: 'success',
        message: `Successfully added labels: ${variables.metadatas.map(({ name }) => name).join(', ')}`,
      });

      if (spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  const { mutate: deleteVectorMetadatasFn } = useMutation({
    mutationFn: (body: DeleteVectorMetadatasBody) =>
      deleteVectorMetadatas(body),
    onMutate: variables => {
      setNotification({
        type: 'warning',
        message: `Deleting label(s): ${variables.metadatas.map(name => name).join(', ')}`,
      });
    },
    onError: (_, variables) => {
      setNotification({
        type: 'error',
        message: `Error deleting label(s): ${variables.metadatas.map(name => name).join(', ')}`,
      });
    },
    onSuccess: (_, variables) => {
      setNotification({
        type: 'success',
        message: `Successfully deleted label(s): ${variables.metadatas.map(name => name).join(', ')}`,
      });

      if (spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  const { mutate: updateVectorMetadatasNameFn } = useMutation({
    mutationFn: (body: UpdateVectorMetadatasNameBody) =>
      updateVectorMetadatasName(body),
    onError: (_, variables) => {
      setNotification({
        type: 'error',
        message: `Error while changing label name to ${variables.metadatas.at(0)?.newName}`,
      });
    },
    onSuccess: (_, variables) => {
      setNotification({
        type: 'success',
        message: `Changed label name from ${variables.metadatas.at(0)?.oldName} to ${variables.metadatas.at(0)?.newName}`,
      });

      if (spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  const { mutate: updateVectorMetadatasValueFn } = useMutation({
    mutationFn: (body: UpdateVectorMetadatasValueBody) =>
      updateVectorMetadatasValue(body),
    onError: (_, variables) => {
      setNotification({
        type: 'error',
        message: `Error while changing label value to ${variables.metadatas.at(0)?.newValue}`,
      });
    },
    onSuccess: (_, variables) => {
      setNotification({
        type: 'success',
        message: `Changed label value to ${variables.metadatas.at(0)?.newValue}`,
      });

      if (spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  const { mutate: deleteNamespaceDimensionsFn } = useMutation({
    mutationFn: ({ body, params }: VectorDeleteDimensionsRequest) =>
      VectorService.deleteDimensions({ body, params }),
    onMutate: variables => {
      setNotification({
        type: 'warning',
        message: `Deleting dimension ${variables.body.dimensions[0].title} from memory pod`,
      });
    },
    onError: (_, variables) => {
      setNotification({
        type: 'error',
        message: `Failed to delete dimension ${variables.body.dimensions[0].title} from memory pod`,
      });
    },
    onSuccess: (_, variables) => {
      if (spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      setNotification({
        type: 'success',
        message: `Successfully deleted dimension ${variables.body.dimensions[0].title} from memory pod`,
      });

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  const isVectorButtonDisabled =
    (!isEditing && stagingFiles.length === 0) || status === 'pending';

  useEffect(() => {
    if (selectedNamespace) {
      setEmbeddingModel(selectedNamespace.embeddingModelName);
      setName(selectedNamespace.name);
      setStoredFiles(selectedNamespace.files);
      setNamespaceDimensions(selectedNamespace.dimensions);
      setSearchUsingDimensions(selectedNamespace.searchUsingDimensions);
    } else if (open && !isEditing) {
      const defaultDimension = {
        id: crypto.randomUUID(),
        title: TIME_DIMENSION.title,
        description: TIME_DIMENSION.description,
        isDefault: true,
      };
      setNamespaceDimensions([defaultDimension]);
    }
  }, [selectedNamespace, open, isEditing]);

  if (!namespaceId) {
    // return null;
  }

  return (
    <VectorModalContext.Provider
      value={{
        spaceId,
        namespaceId,
        tab,

        embeddingModel,
        name,
        searchUsingDimensions,
        onEmbeddingModelChange: handleEmbeddingModelChange,
        onNameChange: handleNameChange,
        onSearchUsingDimensionsChange: handleSearchUsingDimensionsChange,

        stagingFiles,
        selectedStagingFiles,
        onStagingFilesChange: handleStagingFilesChange,
        onStagingFilesAdd: handleStagingFilesAdd,
        onSelectedStagingFilesChange: handleSelectedStagingFilesChange,

        storedFiles,
        selectedStoredFiles,
        onStoredFilesChange: handleStoredFilesChange,
        onSelectedStoredFilesChange: handleSelectedStoredFilesChange,

        namespaceDimensions,
        onNamespaceDimensionsChange: handleNamespaceDimensionsChange,
        onNamespaceDimensionsDelete: handleNamespaceDimensionsDelete,

        onClose: handleCloseDialog,
        onNamespaceCreate: handleNamespaceCreate,
        onNamespaceUpdate: handleNamespaceUpdate,

        onVectorFileDelete: handleVectorFileDelete,

        onMetadataCreate: handleMetadataCreate,
        onMetadataDelete: handleMetadataDelete,
        onMetadataNameChange: handleMetadataNameChange,
        onMetadataValueChange: handleMetadataValueChange,

        isEditing,
      }}
    >
      <Dialog
        open={open}
        fullWidth
        sx={{
          '.MuiPaper-root': {
            minWidth: '1200px',
            maxWidth: '1200px',
            height: '950px',
            borderRadius: '1rem',
          },
        }}
      >
        <Button
          sx={{
            position: 'absolute',
            right: '1.25rem',
            top: '1.25rem',
            padding: '0.25rem',
            minWidth: 'auto',
            zIndex: '10',
          }}
          onClick={() => handleCloseDialog()}
        >
          <span
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              borderWidth: 0,
            }}
          >
            Close
          </span>

          <X width={24} height={24} />
        </Button>

        <DialogContent
          sx={{
            position: 'relative',
            isolation: 'isolate',
            display: 'flex',
            padding: 0,
          }}
        >
          <Tabs
            orientation="vertical"
            aria-label="Vector upload"
            variant="scrollable"
            value={tab}
            onChange={(_, value) => {
              handleTabChange(value as TabName);
            }}
            sx={{
              position: 'sticky',
              top: '0',

              width: '230px',
              backgroundColor: '#F9FAFB',

              borderInlineEndWidth: 1,
              borderInlineEndStyle: 'solid',
              borderInlineEndColor: '#EAECF0',

              paddingBlock: '1.5rem',
              paddingInlineStart: '0.5rem',
              '.MuiTabs-flexContainer': {
                gap: '1rem',
              },
            }}
          >
            <Tab
              label="Embedding"
              value="embedding"
              id="vertical-tab-embedding"
              aria-controls="vertical-tabpanel-embedding"
              icon={<Code style={{ width: '1.5rem', height: '1.5rem' }} />}
              iconPosition="start"
              sx={{
                minHeight: '2.625rem',
                justifyContent: 'start',
                textTransform: 'none',
              }}
            />

            <Tab
              label="Upload Documents"
              value="upload"
              id="vertical-tab-upload"
              aria-controls="vertical-tabpanel-upload"
              icon={<FileText style={{ width: '1.5rem', height: '1.5rem' }} />}
              iconPosition="start"
              sx={{
                minHeight: '2.625rem',
                justifyContent: 'start',
                textTransform: 'none',
              }}
            />

            <Tab
              label="Edit Memory Pod"
              value="edit-stored-files"
              id="vertical-tab-edit-stored-files"
              aria-controls="vertical-tabpanel-edit-stored-files"
              icon={<Database style={{ width: '1.5rem', height: '1.5rem' }} />}
              iconPosition="start"
              disabled={!isEditing}
              sx={{
                minHeight: '2.625rem',
                justifyContent: 'start',
                textTransform: 'none',
              }}
            />

            <Tab
              label="Settings"
              value="settings"
              id={`${modalId}-vertical-tab-settings`}
              aria-controls="vertical-tabpanel-settings"
              icon={<Gear style={{ width: '1.5rem', height: '1.5rem' }} />}
              iconPosition="start"
              sx={{
                minHeight: '2.625rem',
                justifyContent: 'start',
                textTransform: 'none',
              }}
            />
          </Tabs>

          <EmbeddingTabPanel modalId={modalId} selectedTab={tab} />
          <UploadTabPanel modalId={modalId} selectedTab={tab} />
          <EditStoredFilesTabPanel modalId={modalId} selectedTab={tab} />
          <SettingsTabPanel modalId={modalId} selectedTab={tab} />
        </DialogContent>

        <DialogActions
          sx={{
            borderBlockStartWidth: '1px',
            borderBlockStartStyle: 'solid',
            borderBlockStartColor: '#EAECF0',
            display: 'flex',
            gap: '1rem',
          }}
        >
          {['upload', 'edit-stored-files'].includes(tab) &&
          (selectedStagingFiles.length !== 0 ||
            selectedStoredFiles.length !== 0) ? (
            <>
              <Button
                variant="text"
                color="error"
                onClick={() => handleBulkDeleteFiles()}
              >
                Delete Documents
              </Button>

              <Button
                variant="outlined"
                onClick={() => handleDimensionBulkEditModalOpen()}
              >
                Manage Dimensions
              </Button>

              <Button
                variant="outlined"
                onClick={() => handleMetadataBulkEditModalOpen()}
              >
                Manage Labels
              </Button>

              <Button
                variant="outlined"
                disabled={tab === 'edit-stored-files'}
                onClick={() => handleChunkingBulkEditModalOpen()}
              >
                Manage Chunking
              </Button>
            </>
          ) : null}

          <VectorModalButton disabled={isVectorButtonDisabled}>
            <VectorModalButtonLabel buttonStatus={status} />
          </VectorModalButton>
        </DialogActions>

        <VectorDimensionBulkEditModal
          open={dimensionBulkEditModalOpen}
          onOpenChange={handleDimensionBulkEditModalOpen}
        />

        <VectorChunkingBulkEditModal
          open={chunkingBulkEditModalOpen}
          onOpenChange={handleChunkingBulkEditModalOpen}
        />

        <VectorMetadataBulkEditModal
          open={metadataBulkEditModalOpen}
          onOpenChange={handleMetadataBulkEditModalOpen}
        />
      </Dialog>
    </VectorModalContext.Provider>
  );
}

export { BULK_MAX_DISPLAY_ITEMS, selectChunkingOptions, VectorMemoryModal };

